import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarDays, ChevronLeft, ChevronRight, Pencil, Plus, Trash2 } from 'lucide-react';
import { Badge } from '@/shared/components/ui/badge';
import { Button } from '@/shared/components/ui/button';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';
import { Toolbar } from '@/shared/components/custom/toolbar';
import { ConfirmDialog } from '@/shared/components/custom/confirm-dialog/ConfirmDialog';
import { useConfirm } from '@/shared/hooks/useConfirm';
import { formatDisplayDate } from '@/shared/utils/dateUtils';
import { useHolidays } from '../../hooks/useHolidays';
import { useDeleteHoliday } from '../../hooks/useDeleteHoliday';
import { HolidayFormDialog } from './HolidayFormDialog';
import { ReseedHolidaysButton } from './ReseedHolidaysButton';
import type { HolidayDto } from '../../types';

export function HolidaysTable() {
  const { t } = useTranslation('timeOff');
  const [year, setYear] = useState(new Date().getFullYear());
  const { data, isLoading, isError } = useHolidays({ year });
  const deleteHoliday = useDeleteHoliday();
  const { confirm, isOpen, options, handleConfirm, handleCancel } = useConfirm();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState<HolidayDto | null>(null);

  function openCreate() {
    setEditingHoliday(null);
    setDialogOpen(true);
  }

  function openEdit(holiday: HolidayDto) {
    setEditingHoliday(holiday);
    setDialogOpen(true);
  }

  async function handleDelete(holiday: HolidayDto) {
    if (!holiday.id) return;
    const result = await confirm({
      title: t('settings.holidays.delete'),
      description: holiday.name ?? undefined,
      variant: 'destructive',
    });
    if (!result.confirmed) return;
    deleteHoliday.mutate(holiday.id);
  }

  return (
    <div className="space-y-3" data-testid="time-off-settings-holidays-table">
      <Toolbar>
        <Toolbar.Info
          title={t('settings.holidays.title')}
          description={t('settings.holidays.description')}
        />
        <Toolbar.Actions>
          <div className="flex items-center gap-1 rounded-lg border bg-background p-1.5">
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              onClick={() => setYear((y) => y - 1)}
              aria-label={t('settings.holidays.prevYear')}
              data-testid="time-off-settings-holidays-prev-year"
            >
              <ChevronLeft className="size-4" />
            </Button>
            <span
              className="w-12 text-center text-sm font-medium"
              data-testid="time-off-settings-holidays-year"
            >
              {year}
            </span>
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              onClick={() => setYear((y) => y + 1)}
              aria-label={t('settings.holidays.nextYear')}
              data-testid="time-off-settings-holidays-next-year"
            >
              <ChevronRight className="size-4" />
            </Button>
          </div>
          <ReseedHolidaysButton year={year} />
          <Button onClick={openCreate} data-testid="time-off-settings-holidays-create-btn">
            <Plus className="size-4" />
            {t('settings.holidays.create')}
          </Button>
        </Toolbar.Actions>
      </Toolbar>

      {isLoading && (
        <div
          className="divide-y divide-border rounded-xl border"
          data-testid="time-off-settings-holidays-loading-skeleton"
        >
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="flex items-center justify-between gap-3 px-5 py-4">
              <div className="flex-1 space-y-2">
                <Skeleton className="h-4 w-1/3" />
                <Skeleton className="h-3 w-1/4" />
              </div>
            </div>
          ))}
        </div>
      )}

      {isError && (
        <p className="text-sm text-muted-foreground" data-testid="time-off-settings-holidays-error">
          {t('apiErrors.holidays.default')}
        </p>
      )}

      {!isLoading && !isError && (
        <div className="divide-y divide-border rounded-xl border bg-card">
          {(data ?? []).map((holiday) => (
            <div
              key={holiday.id}
              className="flex items-center justify-between gap-3 py-3.5 pr-4 pl-5"
              data-testid={`time-off-holiday-row-${holiday.id}`}
            >
              <div className="flex min-w-0 flex-1 items-center gap-3">
                <CalendarDays className="size-4 shrink-0 text-muted-foreground" />
                <span className="truncate text-[15px] font-medium">{holiday.name}</span>
                <span className="shrink-0 text-sm text-muted-foreground">
                  {formatDisplayDate(holiday.date)}
                </span>
              </div>

              <div className="flex shrink-0 items-center gap-3">
                <Badge
                  variant="outline"
                  className={
                    holiday.isOfficial
                      ? 'w-fit border-transparent bg-primary/10 text-primary'
                      : 'w-fit border-transparent bg-secondary text-secondary-foreground'
                  }
                >
                  {holiday.isOfficial
                    ? t('settings.holidays.official')
                    : t('settings.holidays.company')}
                </Badge>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      onClick={() => openEdit(holiday)}
                      aria-label={t('settings.holidays.edit')}
                      data-testid={`time-off-holiday-edit-${holiday.id}`}
                    >
                      <Pencil className="size-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{t('settings.holidays.edit')}</TooltipContent>
                </Tooltip>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      onClick={() => handleDelete(holiday)}
                      aria-label={t('settings.holidays.delete')}
                      className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                      data-testid={`time-off-holiday-delete-${holiday.id}`}
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{t('settings.holidays.delete')}</TooltipContent>
                </Tooltip>
              </div>
            </div>
          ))}
        </div>
      )}

      <HolidayFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        editingHoliday={editingHoliday}
      />

      {isOpen && options && (
        <ConfirmDialog
          open={isOpen}
          onOpenChange={(open) => !open && handleCancel()}
          title={options.title}
          description={options.description}
          confirmLabel={options.confirmLabel}
          cancelLabel={options.cancelLabel}
          variant={options.variant}
          onConfirm={handleConfirm}
        />
      )}
    </div>
  );
}
